import { useNavigate } from 'react-router-dom';
import {
  Users, Zap, UserPlus, UserMinus, FileDown,
  Cake, Award, ArrowRight,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { PageHeader } from '../components/PageHeader';
import { Avatar, Badge, Button, Card, CardBody, CardHeader, EmptyState } from '../components/ui';
import { HBar } from '../components/charts';
import { useDashboard } from '../api/queries';
import { fmtDate, initials, relativeTime, AUDIT_ACTION_META } from '../lib/format';
import type { CurrentUser } from '../api/types';
import { AuditIcon } from '../components/AuditIcon';
import { downloadFile } from '../api/client';
import { useToast } from '../lib/toast';

export function DashboardScreen({ user, onCreateMember }: { user: CurrentUser; onCreateMember: () => void }) {
  const nav = useNavigate();
  const toast = useToast();
  const { data, isLoading } = useDashboard();

  const exportList = async () => {
    try {
      await downloadFile('/members/export.csv', 'mitglieder.csv');
      toast.push('CSV heruntergeladen');
    } catch {
      toast.push('Export fehlgeschlagen', 'warn');
    }
  };

  const hour = new Date().getHours();
  const gruss = hour < 11 ? 'Guten Morgen' : hour < 17 ? 'Servus' : 'Guten Abend';
  const dateStr = new Date().toLocaleDateString('de-AT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  if (isLoading || !data) {
    return (
      <div className="page-fade">
        <PageHeader title={`${gruss}, ${user.vorname}!`} subtitle="Lade Daten…" />
        <div className="grid-4">{[0, 1, 2, 3].map((i) => <div key={i} className="skeleton" style={{ height: 90, borderRadius: 12 }} />)}</div>
      </div>
    );
  }

  const sectionData = (data.sektionen ?? []).map((s) => ({ label: s.name, value: s.count, dot: s.color }));
  const pct = data.total ? Math.round((data.active / data.total) * 100) : 0;
  const month = new Date().toLocaleDateString('de-AT', { month: 'long' });

  return (
    <div className="page-fade">
      <PageHeader
        title={`${gruss}, ${user.vorname}!`}
        subtitle={`${dateStr} · schön, dass du da bist.`}
        actions={
          <>
            <Button variant="secondary" icon={<FileDown size={16} />} onClick={exportList}>Liste exportieren</Button>
            <Button variant="primary" icon={<UserPlus size={16} />} onClick={onCreateMember}>Neues Mitglied</Button>
          </>
        }
      />

      <div className="grid-4" style={{ marginBottom: 20 }}>
        <Kpi label="Mitglieder gesamt" value={data.total} icon={<Users size={16} />} color="#007AFF" />
        <Kpi label="Aktive" value={data.active} icon={<Zap size={16} />} color="#2DA44E" sub={`${pct} % der Mitglieder`} />
        <Kpi label={`Neu ${new Date().getFullYear()}`} value={data.neuJahr} icon={<UserPlus size={16} />} color="#7C3AED" sub="dieses Vereinsjahr" />
        <Kpi label="Ausgetreten" value={data.ausgetreten} icon={<UserMinus size={16} />} color="#86868B" sub="gesamt" />
      </div>

      <div className="dash-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <Card>
          <CardHeader>{`Geburtstage im ${month}`}</CardHeader>
          <CardBody style={{ padding: 14 }}>
            {data.geburtstage.length === 0 ? (
              <EmptyState icon={<Cake size={20} />} title="Diesen Monat keine Geburtstage" />
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {data.geburtstage.slice(0, 6).map((m) => {
                  const day = Number(m.geburtsdatum.slice(8, 10));
                  const turns = new Date().getFullYear() - Number(m.geburtsdatum.slice(0, 4));
                  return (
                    <button key={m.id} className="person-chip" onClick={() => nav(`/mitglieder/${m.id}`)}
                      style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', cursor: 'pointer' }}>
                      <div style={{ position: 'relative' }}>
                        <Avatar name={initials(m.vorname, m.nachname)} />
                        <span style={{
                          position: 'absolute', bottom: -3, right: -3,
                          background: 'var(--accent)', color: '#fff',
                          borderRadius: 999, width: 18, height: 18,
                          display: 'grid', placeItems: 'center',
                          fontSize: 9.5, fontWeight: 700, border: '2px solid var(--bg-surface)',
                        }}>
                          {day}
                        </span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                        <div style={{ fontSize: 13.5, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {m.vorname} {m.nachname}
                        </div>
                        <div className="text-tertiary" style={{ fontSize: 12 }}>wird {turns}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>Anstehende Jubiläen</CardHeader>
          <CardBody style={{ padding: 14 }}>
            {data.jubilaeen.length === 0 ? (
              <EmptyState icon={<Award size={20} />} title="Keine Jubiläen heuer" />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {data.jubilaeen.slice(0, 5).map((m) => (
                  <button key={m.id} className="person-chip" onClick={() => nav(`/mitglieder/${m.id}`)} style={{ cursor: 'pointer' }}>
                    <Avatar name={initials(m.vorname, m.nachname)} />
                    <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                      <div style={{ fontSize: 13.5, fontWeight: 500 }}>{m.vorname} {m.nachname}</div>
                      <div className="text-tertiary" style={{ fontSize: 12 }}>seit {fmtDate(m.eingetreten_am)}</div>
                    </div>
                    <Badge
                      tone={m.jahre === 50 ? 'warning' : m.jahre === 25 ? 'accent' : 'success'}
                    >
                      <Award size={12} /> {m.jahre} Jahre
                    </Badge>
                  </button>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      <div className="dash-2col" style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 20 }}>
        <Card>
          <CardHeader
            action={<Button variant="ghost" size="sm" iconRight={<ArrowRight size={14} />} onClick={() => nav('/statistik')}>Statistik</Button>}
          >
            Mitglieder pro Sektion
          </CardHeader>
          <CardBody>
            <HBar data={sectionData} />
          </CardBody>
        </Card>

        <Card>
          <CardHeader>Letzte Änderungen</CardHeader>
          <div>
            {data.recent.length === 0 && (
              <div className="empty-state" style={{ padding: '24px 16px' }}>
                <p>Noch keine Aktivitäten.</p>
              </div>
            )}
            {data.recent.map((log) => {
              const meta = AUDIT_ACTION_META[log.aktion] ?? { tone: 'neutral' as const, icon: 'circle', label: log.aktion };
              return (
                <div key={log.id} className="activity-row">
                  <div className={`ic ${meta.tone}`}><AuditIcon name={meta.icon} /></div>
                  <div className="text">
                    <span className="who">{log.u_vorname} {log.u_nachname}</span> · {log.feld}{' '}
                    <span className="text-tertiary">({log.tabelle})</span>
                    <div className="when">{relativeTime(log.timestamp)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, icon, color }: { label: string; value: number; sub?: string; icon: ReactNode; color: string }) {
  return (
    <div className="stat" style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div
          style={{
            width: 32, height: 32, borderRadius: 9,
            background: `color-mix(in srgb, ${color} 14%, transparent)`,
            color,
            display: 'grid', placeItems: 'center',
          }}
        >
          {icon}
        </div>
        <div className="label" style={{ marginBottom: 0 }}>{label}</div>
      </div>
      <div className="value" style={{ marginTop: 10 }}>{value}</div>
      {sub && <div className="text-tertiary" style={{ fontSize: 12.5 }}>{sub}</div>}
    </div>
  );
}
